import { ReactElement, ReactNode, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { isEqual } from 'lodash-es'
import { AnyObjectSchema } from 'yup'
import { Stack } from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid'
import { useIsMutating } from '@tanstack/react-query'
import { Form2 } from '@ui/forms'
import CheckSettingsDirtyBlocker from './components/CheckSettingsDirtyBlocker'
import CheckSettingsElementTable from './components/CheckSettingsElementTable'
import CheckSettingsResetButton from './components/CheckSettingsResetButton'
import CheckSettingsSubmitButton from './components/CheckSettingsSubmitButton'
import SharedSettingsFields from './components/SharedSettingsFields'
import { CheckSettingsSectionKey } from './constants'
import { checkSettingsRecomputeKey } from './hooks/useLiveCheckRecompute'
import useSaveSharedCheckSettings from './hooks/useSaveSharedCheckSettings'
import { CheckSettingsFormValues, CheckSettingsRow, MultiEditCheckSettingsElement } from './types'

interface Props {
  elements: MultiEditCheckSettingsElement[]
  /** Distinguishes this section from the other tabs, e.g. for the recompute mutation key. */
  sectionKey: CheckSettingsSectionKey
  /** German plural label used in the snackbars, e.g. 'Heißbemessungs-Einstellungen'. */
  settingsLabel: string
  /** Selects the slice this section edits — drives the "do all members share a value" test. */
  getSettings: (settings: SettingsOnMember | undefined) => unknown
  /** Ensures the staged settings carry the section's object so its fields never read null. */
  normalizeSettings: (settings: SettingsOnMember) => SettingsOnMember
  /** Applies the staged settings to one member, leaving the rest of its settings alone. */
  mergeSettings: (existing: SettingsOnMember, shared: SettingsOnMember) => SettingsOnMember
  /** German warning shown while the selected members differ. */
  differingSettingsMessage: string
  /** Blocks saving and the live recompute while the staged settings are incomplete. */
  isFormValid?: (settings: SettingsOnMember | undefined) => boolean
  validationSchema?: AnyObjectSchema
  /** Columns describing this section's settings in the utilization table. */
  settingsColumns: GridColDef<CheckSettingsRow>[]
  buildSettingsRow: (
    element: MultiEditCheckSettingsElement,
    sharedSettings: SettingsOnMember | undefined,
  ) => Record<string, string>
  'data-cy'?: string
  changeButtonDataCy?: string
  /** The section's form fields, registered under the `sharedSettings` path. */
  fields: ReactNode
}

/**
 * Form shell shared by every member check settings multi-edit section: stages one
 * settings object for the whole selection, falls back to an explicit overwrite
 * when the members differ, previews the effect on each member's utilization and
 * saves the staged settings member by member.
 */
const MultiEditCheckSettings = ({
  elements,
  sectionKey,
  settingsLabel,
  getSettings,
  normalizeSettings,
  mergeSettings,
  differingSettingsMessage,
  isFormValid,
  validationSchema,
  settingsColumns,
  buildSettingsRow,
  'data-cy': dataCy,
  changeButtonDataCy,
  fields,
}: Props): ReactElement => {
  const { projectId } = useParams()

  const { save, isLoading } = useSaveSharedCheckSettings({ settingsLabel, mergeSettings })

  const equalSettings = useMemo(() => {
    if (elements.length === 0) return false

    const reference = getSettings(elements[0].checkSettings)
    return elements.every(({ checkSettings }) => isEqual(getSettings(checkSettings), reference))
  }, [elements, getSettings])

  const defaultValues = useMemo<CheckSettingsFormValues>(
    () => ({
      sharedSettings: equalSettings ? normalizeSettings(elements[0].checkSettings) : undefined,
      elements,
    }),
    [equalSettings, elements, normalizeSettings],
  )

  // The table recomputes the checks live while the shared settings change; saving
  // mid-recompute would persist the stale preview.
  const isUpdatingChecks = useIsMutating(checkSettingsRecomputeKey(projectId, sectionKey)) > 0

  return (
    <Stack direction="column" spacing={2} width="100%" pt={1}>
      <Form2
        key={JSON.stringify(defaultValues)}
        defaultValues={defaultValues}
        validationSchema={validationSchema}
        mode="onChange"
        onSubmit={data => save(data as CheckSettingsFormValues)}
        data-cy={dataCy}
      >
        <Stack direction="column" spacing={1}>
          <CheckSettingsDirtyBlocker sectionKey={sectionKey} />

          <SharedSettingsFields
            normalizeSettings={normalizeSettings}
            differingSettingsMessage={differingSettingsMessage}
            changeButtonDataCy={changeButtonDataCy}
          >
            {fields}
          </SharedSettingsFields>

          <CheckSettingsElementTable
            sectionKey={sectionKey}
            settingsColumns={settingsColumns}
            buildSettingsRow={buildSettingsRow}
            mergeSettings={mergeSettings}
            isFormValid={isFormValid}
          />

          <Stack direction="row" spacing={1}>
            <CheckSettingsResetButton disabled={isUpdatingChecks || isLoading} />
            <CheckSettingsSubmitButton loading={isLoading} disabled={isUpdatingChecks} />
          </Stack>
        </Stack>
      </Form2>
    </Stack>
  )
}

export default MultiEditCheckSettings
